import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum, type Site } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { z } from 'zod/v4'
import {
  APP_VERSION,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  SEEN_API_URL,
} from '#src/config.ts'
import { startMemorySampling } from '#src/memory.ts'
import {
  enrichActionEventWithAppointmentKind,
  RECORDED_INTERACTION_EVENT,
  recordAppointmentKindOnInteraction,
} from '#src/rumActionContext.ts'

// Disable zod v4's JIT schema compilation. The JIT path probes for eval
// support via `new Function("")` (see `zod/v4/core/util.js` `allowsEval`),
// which trips CSP `script-src` reports under our policy. With `jitless: true`
// the probe short-circuits before it ever runs. Must be set before any v4
// schemas are created — `instrument.ts` is the first import in `Main.tsx`.
// Upstream guidance: https://github.com/colinhacks/zod/issues/4461
z.config({ jitless: true })

// Benign browser quirk: layout-cycle warning that doesn't break anything.
// Browsers fire this when ResizeObserver callbacks would loop; the observer
// simply defers delivery to the next frame. Not actionable.
const RESIZE_OBSERVER_NOISE =
  'ResizeObserver loop completed with undelivered notifications'

// Medplum's FHIR client `parseBody` rethrows `TypeError: Failed to fetch`
// whenever the browser tears down the connection mid-response (tab close,
// navigation away, offline, sleep/wake). Observed pattern in production:
// server returns HTTP 200, but the body stream aborts client-side, and a
// single affected tab generates hundreds of events in seconds as React Query
// retries multiple resource hooks (`readResourceClass`, `searchResourceClass`,
// `searchResourcePages`, etc.) all unwind through the same `parseBody` frame.
// Not actionable on our end — React Query already handles the rejection.
// Kept tight: exact `Failed to fetch` message AND the stack must mention
// `parseBody` from `@medplum/core` so unrelated fetch bugs still surface.
// Datadog issue: d256c10a-3de9-11f1-b757-da7ad0900003.
const isMedplumParseBodyFetchFailure = (
  message: string | undefined,
  stack: string | undefined
): boolean => {
  if (message !== 'Failed to fetch') {
    return false
  }
  if (!stack) {
    return false
  }
  return stack.includes('parseBody') && stack.includes('@medplum/core')
}

if (DATADOG_APPLICATION_ID && DATADOG_CLIENT_TOKEN) {
  // Initialize Datadog Logs
  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE as Site,
    service: 'orbit-web',
    env: __APP_ENV__,
    version: APP_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['log', 'info', 'warn', 'error'],
    forwardReports: ['intervention', 'deprecation', 'csp_violation'],
    sessionSampleRate: 100,
    beforeSend: (log) => {
      if (log.message.includes(RESIZE_OBSERVER_NOISE)) {
        return false
      }
      if (
        isMedplumParseBodyFetchFailure(log.error?.message, log.error?.stack)
      ) {
        return false
      }
      return true
    },
  })

  // Initialize Datadog RUM
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE as Site,
    service: 'orbit-web',
    env: __APP_ENV__,
    version: APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Views and errors carry feature flag evaluations by default; this adds
    // actions, so they can be split by flag state too.
    trackFeatureFlagsForEvents: ['action'],
    defaultPrivacyLevel: 'mask',
    plugins: [reactPlugin({ router: true })],
    allowedTracingUrls: [
      /^http:\/\/localhost:3000/,
      /^https:\/\/seenehr.com/,
      /^https:\/\/seenehr.dev/,
      /^https:\/\/api.medplum.com/,
      new RegExp(`^${SEEN_API_URL}`),
    ],
    traceSampleRate: 100,
    profilingSampleRate: 100,
    beforeSend: (event, context) => {
      if (event.type === 'error') {
        if (event.error.message.includes(RESIZE_OBSERVER_NOISE)) {
          return false
        }
        if (
          isMedplumParseBodyFetchFailure(event.error.message, event.error.stack)
        ) {
          return false
        }
      }
      enrichActionEventWithAppointmentKind(event, context)
      return true
    },
  })

  // Capture phase, so the appointment kind is read before any handler can
  // navigate or re-render the interacted element out of the document.
  document.addEventListener(
    RECORDED_INTERACTION_EVENT,
    recordAppointmentKindOnInteraction,
    true
  )

  startMemorySampling()
}
